import { z } from "zod";

/**
 * Boundary validation for the Microsoft 365 Entra app provisioning workflow.
 * The API is authoritative; the web app re-uses `startProvisioningSchema` for
 * optimistic client-side checks. No secret, token, or Hudu key is validated
 * here — those never appear in web-facing request bodies.
 */

export const EXISTING_APP_BEHAVIORS = ["cancel", "reuse", "create-new"] as const;

export const provisioningStageSchema = z.enum([
  "waiting_for_signin",
  "authenticating",
  "authenticated",
  "discovering_permissions",
  "creating_application",
  "creating_service_principal",
  "adding_permissions",
  "granting_consent",
  "saving_to_hudu",
  "completed",
  "failed",
  "cancelled",
]);

export const provisioningStageStatusSchema = z.enum([
  "running",
  "success",
  "warning",
  "failed",
  "skipped",
]);

export const provisioningErrorCodeSchema = z.enum([
  "AUTH_FAILED",
  "AUTH_STATE_MISMATCH",
  "AUTH_PKCE_INVALID",
  "AUTH_CANCELLED",
  "BROWSER_CLOSED",
  "SESSION_NOT_ACTIVE",
  "GRAPH_ERROR",
  "GRAPH_THROTTLED",
  "DUPLICATE_APP_EXISTS",
  "HUDU_NOT_CONFIGURED",
  "HUDU_COMPANY_NOT_FOUND",
  "HUDU_SAVE_FAILED",
  "PROVISION_FAILED",
  "CANCELLED",
]);

/** POST /api/v1/browser-sessions/:id/provision (web → API). */
export const startProvisioningSchema = z.object({
  existingAppBehavior: z.enum(EXISTING_APP_BEHAVIORS).default("cancel"),
});

export type StartProvisioningInput = z.infer<typeof startProvisioningSchema>;

// ── Agent callback payloads (authenticated with the session's own token) ──────

const agentIdentitySchema = z.object({
  agentVersion: z.string().trim().min(1).max(50),
  workstationId: z.string().trim().min(1).max(200),
});

export const agentProvisionStartedSchema = agentIdentitySchema;

export const agentProvisionProgressSchema = agentIdentitySchema.extend({
  stage: provisioningStageSchema,
  stageStatus: provisioningStageStatusSchema,
  message: z.string().trim().max(500),
  tenantId: z.string().trim().min(1).max(100).optional(),
});

const classifiedPermissionSchema = z.object({
  value: z.string().trim().min(1).max(200),
  reason: z.string().trim().max(300),
});

export const sanitizedPermissionSummarySchema = z.object({
  inspected: z.number().int().nonnegative(),
  readOnly: z.number().int().nonnegative(),
  assigned: z.number().int().nonnegative(),
  included: z.array(z.string().trim().min(1).max(200)).max(1000),
  excluded: z.array(classifiedPermissionSchema).max(2000),
  ambiguous: z.array(classifiedPermissionSchema).max(2000),
  notFoundOrRejected: z.array(z.string().trim().min(1).max(200)).max(1000),
  consentFailures: z.array(z.string().trim().min(1).max(200)).max(1000),
});

const existingAppConflictSchema = z.object({
  applicationObjectId: z.string().trim().min(1).max(100),
  applicationClientId: z.string().trim().min(1).max(100),
  displayName: z.string().trim().min(1).max(300),
});

export const agentProvisionResultSchema = agentIdentitySchema.extend({
  idempotencyKey: z.string().trim().min(16).max(200),
  tenantId: z.string().trim().min(1).max(100),
  tenantDisplayName: z.string().trim().max(300).nullable(),
  applicationObjectId: z.string().trim().min(1).max(100).nullable(),
  applicationClientId: z.string().trim().min(1).max(100).nullable(),
  servicePrincipalId: z.string().trim().min(1).max(100).nullable(),
  clientSecret: z.string().min(1).max(2000).nullable(),
  secretExpiresAt: z.string().datetime().nullable(),
  permissionSummary: sanitizedPermissionSummarySchema.nullable(),
  existingAppConflict: existingAppConflictSchema.nullable().optional(),
});

export const agentProvisionFailedSchema = agentIdentitySchema.extend({
  errorCode: provisioningErrorCodeSchema,
  stage: provisioningStageSchema.optional(),
  tenantId: z.string().trim().min(1).max(100).optional(),
  partialState: z
    .object({
      applicationObjectId: z.string().trim().max(100).nullable().optional(),
      applicationClientId: z.string().trim().max(100).nullable().optional(),
      servicePrincipalId: z.string().trim().max(100).nullable().optional(),
    })
    .optional(),
});
